// src/components/ForgotPassword.js
import React, { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Logo from "../../../assets/Logo.svg";
import Heart from "../../../assets/images/envelope 1.png";
import styles from "../AuthWrapper.module.css";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";
import { showToast } from "../../../utils/toast";
import { forgotPasswordAPI } from "../../../api/userApi";

function ForgotPassword() {
  const seo = useSEO("forgot-password");
  const history = useHistory();
  const location = useLocation();
  const [email, setEmail] = useState<string>("");
  const [inputEmail, setInputEmail] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get("email")?.trim() || "";
    if (emailParam) {
      setEmail(emailParam);
      setInputEmail(emailParam);
    }
  }, [location.search]);

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = useCallback(async () => {
    const emailToUse = email || inputEmail.trim();

    if (!emailToUse) {
      showToast("Please enter your email address.", "error");
      return;
    }

    if (!isValidEmail(emailToUse)) {
      showToast("Please enter a valid email address.", "error");
      return;
    }

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await forgotPasswordAPI({ email: emailToUse });
      // Always show the same message regardless of whether account exists
      showToast(
        "If an account with that email exists, a password reset link has been sent.",
        "success"
      );
      setEmailSent(true);
      setEmail(emailToUse);
      setInputEmail(emailToUse);
      // Update URL without reloading
      history.replace(
        `/forgot-password?email=${encodeURIComponent(emailToUse)}`
      );
    } catch (error) {
      console.error("Forgot password error:", error);
      // Always show the same message regardless of error (security best practice)
      showToast(
        "If an account with that email exists, a password reset link has been sent.",
        "success"
      );
      setEmailSent(true);
      setEmail(emailToUse);
      setInputEmail(emailToUse);
      history.replace(
        `/forgot-password?email=${encodeURIComponent(emailToUse)}`
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [email, inputEmail, isSubmitting, history]);

  const handleResend = useCallback(async () => {
    const emailToUse = email || inputEmail.trim();
    if (!emailToUse) {
      showToast("No email provided for password reset.", "error");
      return;
    }
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await forgotPasswordAPI({ email: emailToUse });
      // Always show the same message regardless of whether account exists
      showToast(
        "If an account with that email exists, a password reset link has been sent.",
        "success"
      );
    } catch (error) {
      console.error("Forgot password error:", error);
      // Always show the same message regardless of error (security best practice)
      showToast(
        "If an account with that email exists, a password reset link has been sent.",
        "success"
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [email, inputEmail, isSubmitting]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const showConfirmationPage = email && emailSent;

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <div className={styles.authWrapper}>
        <div className={styles.authLeft}>
          <div className={styles.authBox}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src={Logo} alt="SugarCribs" className={styles.logo} />
            </div>
            <div>
              <img src={Heart} alt="SugarCribs" />
            </div>

            {showConfirmationPage ? (
              <>
                <h2
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "32px",
                    fontWeight: 600,
                    color: "#ffffff",
                    marginBottom: 12,
                  }}
                >
                  Restore password
                </h2>
                <p
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "20px",
                    fontWeight: 400,
                    color: "#ffffff",
                    marginBottom: 12,
                  }}
                >
                  We&rsquo;ve sent you a link to create a new password to your
                  email.
                </p>
                <a
                  href={`mailto:${email}`}
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#ffffff",
                    marginBottom: 32,
                  }}
                >
                  {email}
                </a>
                <p className={styles.switchMode}>
                  Haven&rsquo;t received an email?
                  <button
                    type="button"
                    className={styles.linkLikeButton}
                    onClick={handleResend}
                    disabled={isSubmitting}
                    style={{
                      fontSize: 16,
                      marginLeft: 6,
                      border: 0,
                      color: "#FE5A97",
                      padding: "0 8px",
                      textDecoration: "underline",
                      opacity: isSubmitting ? 0.6 : 1,
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                    }}
                  >
                    Click Here
                  </button>
                </p>
              </>
            ) : (
              <>
                <h2
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "32px",
                    fontWeight: 600,
                    color: "#ffffff",
                    marginBottom: 12,
                  }}
                >
                  Forgot password?
                </h2>
                <p
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "20px",
                    fontWeight: 400,
                    color: "#ffffff",
                    marginBottom: 32,
                  }}
                >
                  Enter your email address and we&rsquo;ll send you a link to
                  reset your password.
                </p>
                <label
                  htmlFor="forgot-email"
                  style={{ display: "block", width: "100%" }}
                >
                  <span
                    style={{
                      display: "block",
                      textAlign: "left",
                      marginBottom: 12,
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#ffffff",
                      fontFamily: "TT Norms, sans-serif",
                    }}
                  >
                    E-mail
                  </span>
                  <input
                    id="forgot-email"
                    type="email"
                    placeholder="Enter your email"
                    value={inputEmail}
                    onChange={e => setInputEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className={styles.inputField}
                    disabled={isSubmitting}
                  />
                </label>
                <button
                  className={styles.actionButton}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{
                    marginTop: 24,
                    opacity: isSubmitting ? 0.6 : 1,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  {isSubmitting ? "Sending..." : "Send Reset Link"}
                </button>
                <p className={styles.switchMode}>
                  Remember your password?{" "}
                  <button
                    type="button"
                    className={styles.linkLikeButton}
                    onClick={() => history.push("/login")}
                    style={{
                      fontSize: 16,
                      border: 0,
                      color: "#FE5A97",
                      padding: "0 8px",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Sign in
                  </button>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ForgotPassword;
